/**
 * Identifier normalisation — the portal's half of a rule the backend also holds.
 *
 * **Paired with `backend/src/deca/identifiers.ts`, deliberately at the same relative path.**
 * The two files must agree character for character: the backend refuses or accepts a grouping
 * on `normaliseIdentifier`'d NIFs (`backend/src/deca/grouping.ts`'s `partiesDiffer`), and every
 * portal check that decides whether a request is even worth sending has to mean the same thing
 * by "the same legal person". A raw `!==` on this side refuses a group the server would accept —
 * and there is no way out of that for the user, because `cargador_nif` is a party field and
 * cannot be amended in place (BOE Fifth.2 makes it a supersession). Change one file and you
 * change the other; the backend's SQL mirror in `backend/src/db/repositories.ts` is the third
 * copy and its own docblock says so.
 *
 * This function used to live in `deca/format.ts` labelled *"never for comparison"* and imported
 * by nothing. The rule it implements was never a display rule — it was filed among the
 * formatters, and the label followed the filing rather than the behaviour. It is comparison-safe
 * and always was: it is the same expression the backend compares with. Moved here, next to a
 * name that says what it is for, so the next reader is not warned off the one correct function.
 */

/**
 * Uppercase, non-alphanumerics stripped — so `A-41118829` and `A41118829` are the same NIF/CIF
 * and `1234-BCD`, `1234 bcd` and `1234BCD` are one plate.
 *
 * Strip first, then uppercase — the same order as the backend's copy and its SQL mirror, so that
 * only ASCII survives to be case-folded and `upper()` and `toUpperCase()` cannot disagree about a
 * non-ASCII character. Applied to **both sides** of any comparison that uses it.
 *
 * Absent (`null`/`undefined`) and blank both normalise to `''`, so they compare equal to each
 * other and unequal to any real identifier. That mirrors the backend's `normaliseOrNull`
 * (`grouping.ts`), which maps absent and `''` alike to `null` for the same reason: two documents
 * that both have no trailer are the same combination however the blank was spelled. The backend's
 * `normaliseIdentifier` itself takes a bare `string`; the widening is here rather than in a second
 * helper because every portal field this is applied to is an optional property on a generated
 * type (`cargador_nif?: string`), so `undefined` is the ordinary case, not the edge.
 */
export function normaliseIdentifier(value: string | null | undefined): string {
  return value ? value.replace(/[^0-9A-Za-z]/g, '').toUpperCase() : ''
}

/** Two identifiers naming the same thing, absent included. See `normaliseIdentifier`. */
export function sameIdentifier(a: string | null | undefined, b: string | null | undefined): boolean {
  return normaliseIdentifier(a) === normaliseIdentifier(b)
}
